/**
 * Smart transit sampling for long dasha periods (MD/AD)
 * Takes snapshots only when planets change houses (except Moon)
 * This captures meaningful transit shifts without excessive computation
 */

import { DateTime } from 'luxon';
import type { PlanetName } from './astroEngine';
import { getGrahaLongitudesAndMotionSidereal, getRahuKetuSidereal, assignPlanetsToHouses } from './astroEngine';
import type { AyanamsaProvider } from './ayanamsa';

export type TransitSnapshot = {
  date: string;
  planets: Partial<Record<PlanetName, { house: number; lon: number }>>;
};

/**
 * Sample transits at house-change events for slow planets
 * Excludes Moon (changes house every ~2.5 days = too frequent)
 */
export async function sampleTransitsAtHouseChanges(
  startISO: string,
  endISO: string,
  cusps: number[], // natal cusps for house assignment
  location: { lat: number; lon: number },
  ayanProvider: AyanamsaProvider,
  maxSamples: number = 30 // cap to prevent excessive computation
): Promise<TransitSnapshot[]> {
  const start = DateTime.fromISO(startISO);
  const end = DateTime.fromISO(endISO);
  
  if (!start.isValid || !end.isValid || end <= start) return [];
  
  const samples: TransitSnapshot[] = [];
  const slowPlanets: Array<Exclude<PlanetName, 'Moon'>> = ['Sun', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Rahu', 'Ketu'];
  
  // Track last known house for each planet
  const lastHouse: Partial<Record<PlanetName, number>> = {};
  
  // Always sample start date
  const startSnapshot = await sampleDate(start.toISO()!, cusps, location, ayanProvider);
  if (startSnapshot) {
    samples.push(startSnapshot);
    Object.entries(startSnapshot.planets).forEach(([planet, data]) => {
      lastHouse[planet as PlanetName] = data!.house;
    });
  }
  
  // Determine sampling intervals based on period length
  const totalDays = end.diff(start, 'days').days;
  let stepDays: number;
  
  if (totalDays <= 180) {
    stepDays = 3; // ~3 days for short periods (AD sub-periods)
  } else if (totalDays <= 365) {
    stepDays = 7; // ~weekly for 1 year
  } else if (totalDays <= 1095) {
    stepDays = 14; // ~bi-weekly for 3 years
  } else {
    stepDays = 30; // ~monthly for longer MD periods
  }
  
  let currentDate = start.plus({ days: stepDays });
  
  while (currentDate < end && samples.length < maxSamples) {
    const snapshot = await sampleDate(currentDate.toISO()!, cusps, location, ayanProvider);
    
    if (snapshot) {
      // Check if any slow planet changed house
      let hasHouseChange = false;
      
      for (const planet of slowPlanets) {
        const currentHouse = snapshot.planets[planet]?.house;
        const prevHouse = lastHouse[planet];
        
        if (currentHouse !== undefined && prevHouse !== undefined && currentHouse !== prevHouse) {
          hasHouseChange = true;
          lastHouse[planet] = currentHouse;
        } else if (currentHouse !== undefined && prevHouse === undefined) {
          lastHouse[planet] = currentHouse;
        }
      }
      
      // Only add snapshot if a house change occurred
      if (hasHouseChange) {
        samples.push(snapshot);
      }
    }
    
    currentDate = currentDate.plus({ days: stepDays });
  }
  
  // Always sample end date if not already included
  if (samples.length === 0 || samples[samples.length - 1].date !== end.toISO()) {
    const endSnapshot = await sampleDate(end.toISO()!, cusps, location, ayanProvider);
    if (endSnapshot) samples.push(endSnapshot);
  }
  
  return samples;
}

async function sampleDate(
  isoDate: string,
  cusps: number[],
  location: { lat: number; lon: number },
  ayanProvider: AyanamsaProvider
): Promise<TransitSnapshot | null> {
  try {
    const grahas = getGrahaLongitudesAndMotionSidereal(
      { isoDate, latitude: location.lat, longitude: location.lon },
      ayanProvider
    );
    
    const nodes = getRahuKetuSidereal(
      { isoDate, latitude: location.lat, longitude: location.lon },
      ayanProvider
    );
    
    const planetLongs: Partial<Record<PlanetName, number>> = {
      Sun: grahas.Sun.lon,
      Moon: grahas.Moon.lon,
      Mercury: grahas.Mercury.lon,
      Venus: grahas.Venus.lon,
      Mars: grahas.Mars.lon,
      Jupiter: grahas.Jupiter.lon,
      Saturn: grahas.Saturn.lon,
      Rahu: nodes.Rahu,
      Ketu: nodes.Ketu,
    };
    
    const placements = assignPlanetsToHouses(cusps, planetLongs);
    
    const planets: Partial<Record<PlanetName, { house: number; lon: number }>> = {};
    Object.entries(planetLongs).forEach(([planet, lon]) => {
      if (lon !== undefined) {
        const placement = placements[planet as PlanetName];
        if (placement?.house) {
          planets[planet as PlanetName] = { house: placement.house, lon };
        }
      }
    });
    
    return { date: isoDate, planets };
  } catch (err) {
    return null;
  }
}

/**
 * Calculate aggregate transit statistics from snapshots using blended % scores
 */
export function aggregateTransitStats(
  snapshots: TransitSnapshot[],
  targetHouses: number[],
  functionalClassification?: Record<PlanetName, 'benefic' | 'malefic' | 'neutral' | 'yoga_karaka'>,
  blendedPct?: Partial<Record<PlanetName, number>> // NEW: blended % scores (-100 to +100)
): {
  beneficBlessingPercent: number; // avg benefic support score when aspecting target
  maleficPressurePercent: number; // avg malefic affliction score when aspecting target
  avgTransitsOnTarget: number;    // avg # of planets transiting target houses
} {
  if (snapshots.length === 0) {
    return { beneficBlessingPercent: 0, maleficPressurePercent: 0, avgTransitsOnTarget: 0 };
  }
  
  let totalBeneficScore = 0;
  let totalMaleficScore = 0;
  let snapshotsWithBenefics = 0;
  let snapshotsWithMalefics = 0;
  let totalTransits = 0;
  
  // Use blended % if provided, otherwise fall back to functional classification
  const useBlendedScoring = blendedPct && Object.keys(blendedPct).length > 0;
  
  // Identify functional benefics and malefics
  const benefics: PlanetName[] = functionalClassification
    ? (Object.entries(functionalClassification)
        .filter(([_, cls]) => cls === 'benefic' || cls === 'yoga_karaka')
        .map(([p]) => p as PlanetName))
    : ['Jupiter'];
    
  const malefics: PlanetName[] = functionalClassification
    ? (Object.entries(functionalClassification)
        .filter(([_, cls]) => cls === 'malefic')
        .map(([p]) => p as PlanetName))
    : ['Saturn'];
  
  let debugSample = 0;
  
  for (const snap of snapshots) {
    // Calculate benefic support score (weighted by blended %)
    let snapshotBeneficScore = 0;
    let beneficDetails: string[] = [];
    
    for (const planet of benefics) {
      const house = snap.planets[planet]?.house;
      if (house) {
        const fifthAspect = ((house - 1 + 4) % 12) + 1;
        const ninthAspect = ((house - 1 + 8) % 12) + 1;
        
        let isSupporting = false;
        let aspectType = '';
        
        if (targetHouses.includes(house)) {
          isSupporting = true;
          aspectType = 'conj';
        } else if (targetHouses.includes(fifthAspect)) {
          isSupporting = true;
          aspectType = `5th→H${fifthAspect}`;
        } else if (targetHouses.includes(ninthAspect)) {
          isSupporting = true;
          aspectType = `9th→H${ninthAspect}`;
        }
        
        if (isSupporting) {
          const blendedScore = useBlendedScoring && blendedPct![planet] !== undefined 
            ? Math.abs(blendedPct![planet]!) 
            : 100; // default to 100 if no blended score
          
          snapshotBeneficScore += blendedScore;
          beneficDetails.push(`${planet}@${blendedScore}% (${aspectType})`);
        }
      }
    }
    
    if (snapshotBeneficScore > 0) {
      totalBeneficScore += snapshotBeneficScore;
      snapshotsWithBenefics++;
    }
    
    // Calculate malefic affliction score (weighted by blended %)
    let snapshotMaleficScore = 0;
    let maleficDetails: string[] = [];
    
    for (const planet of malefics) {
      const house = snap.planets[planet]?.house;
      if (house) {
        // Malefics afflict via conjunction + Parashara aspects
        let aspects: number[] = [house]; // conjunction
        
        // Add Parashara aspects based on planet
        if (planet === 'Mars') {
          aspects.push(((house - 1 + 3) % 12) + 1); // 4th
          aspects.push(((house - 1 + 6) % 12) + 1); // 7th
          aspects.push(((house - 1 + 7) % 12) + 1); // 8th
        } else if (planet === 'Saturn') {
          aspects.push(((house - 1 + 2) % 12) + 1); // 3rd
          aspects.push(((house - 1 + 6) % 12) + 1); // 7th
          aspects.push(((house - 1 + 9) % 12) + 1); // 10th
        } else if (planet === 'Rahu' || planet === 'Ketu') {
          aspects.push(((house - 1 + 4) % 12) + 1); // 5th
          aspects.push(((house - 1 + 6) % 12) + 1); // 7th
          aspects.push(((house - 1 + 8) % 12) + 1); // 9th
        } else {
          // Other malefics: 7th aspect only
          aspects.push(((house - 1 + 6) % 12) + 1); // 7th
        }
        
        const hitTargets = targetHouses.filter(h => aspects.includes(h));
        if (hitTargets.length > 0) {
          const blendedScore = useBlendedScoring && blendedPct![planet] !== undefined 
            ? Math.abs(blendedPct![planet]!) 
            : 100; // default to 100 if no blended score
          
          snapshotMaleficScore += blendedScore;
          maleficDetails.push(`${planet}@${blendedScore}% (→${hitTargets.join(',')})`);
        }
      }
    }
    
    if (snapshotMaleficScore > 0) {
      totalMaleficScore += snapshotMaleficScore;
      snapshotsWithMalefics++;
    }
    
    // Count planets in target houses (excluding Moon)
    let snapshotTransits = 0;
    Object.entries(snap.planets).forEach(([planet, data]) => {
      if (planet !== 'Moon' && targetHouses.includes(data!.house)) {
        snapshotTransits++;
      }
    });
    totalTransits += snapshotTransits;
  }
  
  // Calculate average scores and normalize to 0-100% scale
  // Since multiple planets can aspect simultaneously, we normalize by dividing by the theoretical max
  // Theoretical max = (number of benefics/malefics) * 100
  const maxBeneficScore = benefics.length * 100;
  const maxMaleficScore = malefics.length * 100;
  
  const avgBeneficScore = snapshotsWithBenefics > 0 
    ? Math.min(100, Math.round((totalBeneficScore / snapshotsWithBenefics / maxBeneficScore) * 100))
    : 0;
  
  const avgMaleficScore = snapshotsWithMalefics > 0 
    ? Math.min(100, Math.round((totalMaleficScore / snapshotsWithMalefics / maxMaleficScore) * 100))
    : 0;
  
  return {
    beneficBlessingPercent: avgBeneficScore,
    maleficPressurePercent: avgMaleficScore,
    avgTransitsOnTarget: totalTransits / snapshots.length
  };
}
